import { CancelToken } from 'axios';
import _ from 'lodash';
import MUIDataTable, {
  MUIDataTableOptions,
  MUIDataTableProps,
} from 'mui-datatables';
import React, { useCallback, useEffect, useState } from 'react';
import { getCancelTokenSource, isCancel } from '../../services/api';
// import { isBefore, toDate } from '../../utils/date.util';
import TableSkeleton from '../TableSkeleton';

export interface DataTableProps extends MUIDataTableProps {
  dispatchRemote?: (
    page?: number,
    sortOrder?: number,
    cancelToken?: CancelToken,
    limit?: number,
  ) => any;
  onSelect?: (items: any) => any;
  noMatch?: string;
  denseTable?: boolean;
  download?: boolean;
  loading?: boolean;
  print?: boolean;
  filter?: boolean;
  viewColumns?: boolean;
  rows?: number;
  rowsPerPage?: number[];
  selectableRows?: 'none' | 'multiple' | 'single';
}

const CustomDataTable: React.FC<DataTableProps> = (props: DataTableProps) => {
  const [count, setCount] = useState(7);
  const [sortOrder, setSortOrder] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const {
    onSelect,
    dispatchRemote,
    data,
    columns,
    options,
    title,
    noMatch,
    download,
    print,
    filter,
    viewColumns,
    rows,
    loading = false,
    rowsPerPage,
    selectableRows,
    denseTable = false,
    ...rest
  } = props;

  const loadDataFromRemote = useCallback(
    async (pageIn, sortOrderIn, limit: number, cancelToken?: CancelToken) => {
      try {
        if (dispatchRemote) {
          setIsLoading(true);
          const responsePage = await dispatchRemote(
            pageIn,
            sortOrderIn,
            cancelToken,
            limit,
          );
          if (responsePage) {
            setSortOrder(sortOrderIn);
            setCount(responsePage.meta?.totalItems);
            if (onSelect) {
              onSelect(responsePage.data);
            }
          }
          setIsLoading(false);
        }
      } catch (error) {
        if (!isCancel(error)) {
          console.log(error);
        }
      }
    },
    [dispatchRemote, onSelect],
  );

  useEffect(() => {
    const cancelTokenSource = getCancelTokenSource();

    loadDataFromRemote(0, {}, 7, cancelTokenSource.token);

    return () => {
      cancelTokenSource.cancel();
    };
  }, [loadDataFromRemote]);

  const textLabels = {
    body: {
      noMatch: noMatch ?? 'Nenhum registro encontrado',
      toolTip: 'Ordenar',
      columnHeaderTooltip: (column: { label: any }) =>
        `Ordenação para ${column.label}`,
    },
    pagination: {
      next: 'Próxima Página',
      previous: 'Página Anterior',
      rowsPerPage: 'Nº de linhas por página:',
      displayRows: 'de um total de:',
      jumpToPage: 'Ir para a página:',
    },
    toolbar: {
      search: 'Buscar na Tabela',
      downloadCsv: 'Download CSV',
      print: 'Imprimir',
      viewColumns: 'Visualizar Colunas',
      filterTable: 'Filtrar Tabela',
    },
    filter: {
      all: 'Todos',
      title: 'FILTROS',
      reset: 'REDEFINIR',
    },
    selectedRows: {
      text: 'linha(s) selecionadas',
      delete: 'Remover',
      deleteAria: 'Remover Linhas Selecionadas',
    },
  };

  const onTableChange = useCallback(
    (action: any, tableState: any) => {
      switch (action) {
        case 'changePage':
          loadDataFromRemote(
            tableState.page + 1,
            tableState.sortOrder,
            tableState.rowsPerPage,
          );
          break;
        default:
          break;
      }
    },
    [loadDataFromRemote],
  );

  const defaultTableOptions: MUIDataTableOptions = {
    download: download || false,
    filter: filter || false,
    responsive: 'standard',
    print: print || false,
    selectableRows: selectableRows || 'none',
    viewColumns: viewColumns || false,
    enableNestedDataAccess: '.',
    textLabels,
    rowsPerPage: rows || 5,
    rowsPerPageOptions: rowsPerPage || [5, 20, 50, 100],
    setTableProps: () => {
      return {
        padding: denseTable ? 'none' : 'default',
        size: denseTable ? 'small' : 'medium',
      };
    },
    // eslint-disable-next-line no-shadow
    // customSort: (data: any[], colIndex: number, order: string) => {
    //   return data.sort((a: any, b: any) => {
    //     const firstDate = toDate(a.data[colIndex]);
    //     const secondDate = toDate(b.data[colIndex]);
    //     if (firstDate && secondDate) {
    //       return (
    //         (isBefore(firstDate, secondDate) ? -1 : 1) *
    //         (order === 'desc' ? 1 : -1)
    //       );
    //     }
    //     return (
    //       (a.data[colIndex] < b.data[colIndex] ? -1 : 1) *
    //       (order === 'desc' ? 1 : -1)
    //     );
    //   });
    // },
    ...(options?.serverSide
      ? {
          count,
          sortOrder,
          onTableChange,
        }
      : {}),
    ...options,
  } as MUIDataTableOptions;

  return (
    <>
      {isLoading || loading ? (
        <TableSkeleton rows={4} />
      ) : (
        <MUIDataTable
          {...rest}
          title={title}
          data={data}
          columns={columns}
          options={defaultTableOptions}
        />
      )}
    </>
  );
};

export default React.memo(CustomDataTable, (prevProps, nextProps) => {
  let equalTitle = false;
  const { title: prevTitle, data: prevData, options: prevOptions } = prevProps;
  const { title: nextTitle, data: nextData, options: nextOptions } = nextProps;

  if (React.isValidElement(prevTitle) && React.isValidElement(nextTitle)) {
    equalTitle =
      prevTitle.props.children[0] === nextTitle.props.children[0] &&
      prevTitle.props.children[1] === nextTitle.props.children[1];
  } else {
    equalTitle = prevTitle === nextTitle;
  }
  const equalData = _.isEqual(prevData, nextData);

  const equalToolbar = _.isEqual(
    prevOptions?.customToolbar,
    nextOptions?.customToolbar,
  );

  const equalOptions = _.isEqual(
    prevOptions?.rowsSelected,
    nextOptions?.rowsSelected,
  );

  return equalTitle && equalData && equalOptions && equalToolbar;
});
